import { useField, useFormikContext } from "formik";
import { DatePicker, Flex, Switch, Text } from "@jsq/ds/core";
import { FormSelect } from "@jsq/ds/forms";
import { SPACING } from "~/styles/variables";
import { PARAMETER_OPTIONS } from "./constants";
import { formatIsoDate, parseIsoDate } from "./dateHelpers";
import { seedLegDateMode, seedLegDefaults } from "./legSeed";
import { CUSTOM_PARAMETER_HELP_TEXT } from "./periodHelpText";
import * as styles from "./styles_vanilla.css";

type DateLegProps = {
  /** Label rendered above the leg ("Date" or "Start date" / "End date"). */
  legLabel: string;
  /** Formik field name for the parameter string. */
  parameterName: string;
  /** Formik field name for the fixed ISO `yyyy-mm-dd` date string. */
  dateName: string;
  isReadOnly?: boolean;
};

const PARAMETER_OPTIONS_AVAILABLE = PARAMETER_OPTIONS.length > 0;

const renderParameterOptions = () => (
  <FormSelect.Section>
    {PARAMETER_OPTIONS.map((option) => (
      <FormSelect.Option key={option.value} value={option.value}>
        {option.label}
      </FormSelect.Option>
    ))}
  </FormSelect.Section>
);

/** A single date leg used by the CUSTOM body. The `Use parameter` switch is
 * derived from "is the parameter field non-null" so consumers don't track an
 * extra boolean. Toggling seeds the opposite field with a sensible default
 * (first parameter / today's date) so the BE per-leg invariant ("exactly one
 * of param / date is set") always holds. */
export const DateLeg = ({
  legLabel,
  parameterName,
  dateName,
  isReadOnly = false,
}: DateLegProps) => {
  const { setFieldValue, setFieldTouched } = useFormikContext();
  const [{ value: parameterValue }] = useField<string | null>(parameterName);
  const [{ value: dateValue }, dateMeta] = useField<string | null>(dateName);

  const useParameter = parameterValue !== null;
  const switchId = `${parameterName}-use-parameter`;
  const selectLabelId = `${parameterName}-parameter-label`;

  const handleToggle = (next: boolean) => {
    const seed = next ? seedLegDefaults() : seedLegDateMode();
    void setFieldValue(parameterName, seed.parameter, false);
    void setFieldValue(dateName, seed.date, false);
  };

  return (
    <Flex flexDirection="column" gap={SPACING.SMALL}>
      <Text variant="strong-md">{legLabel}</Text>
      <Switch
        id={switchId}
        label="Use parameter"
        labelPosition="right"
        checked={useParameter}
        onChange={(event) => handleToggle(event.target.checked)}
        size="small"
        disabled={isReadOnly || !PARAMETER_OPTIONS_AVAILABLE}
        data-testid={`${parameterName}-use-parameter-switch`}
      />
      {useParameter ? (
        <FormSelect
          name={parameterName}
          label={<span id={selectLabelId}>{`${legLabel} parameter`}</span>}
          hideLabel
          className={styles.formControl}
          disabled={isReadOnly}
          helpText={CUSTOM_PARAMETER_HELP_TEXT}
          data-testid={`${parameterName}-parameter-select`}
        >
          {renderParameterOptions()}
        </FormSelect>
      ) : (
        <div className={styles.formControl}>
          <div data-testid={`${dateName}-date-input`}>
            <DatePicker
              isFull
              hasIcon
              disabled={isReadOnly}
              selected={parseIsoDate(dateValue)}
              onChange={(date) => void setFieldValue(dateName, formatIsoDate(date), false)}
              onBlur={() => void setFieldTouched(dateName, true)}
              errorMessage={dateMeta.touched ? dateMeta.error : undefined}
            />
          </div>
        </div>
      )}
    </Flex>
  );
};
